/* -----------------Helpers & Hooks--------------- */
import { useExamContext } from "@features/exam/contexts/ExamContext";
/* -----------------UI--------------- */
import Box from "@memorang/ui/src/components/Box";
import Loader from "@memorang/ui/src/components/Loader";
import ScrollContainer from "@memorang/ui/src/components/ScrollContainer";
import CustomAppbar from "components/CustomAppbar";

import useBreakpoints from "@memorang/ui/src/useBreakpoints";

/* -----------------Types--------------- */
import type { PracticeTestExam } from "@memorang/types/src/tag";

/* -----------------Child components--------------- */
import { PracticeTestsView } from "../components/PracticeTestsView";

import { appBarMeta } from "@constants/appbar";
import { isWeb } from "@helpers/platform";
/* -----------------Helpers--------------- */
import { getPracticeTestsData } from "../helpers/practice-tests-data";

import CollectUserDetailsDialog from "@features/account/components/dialogs/CollectUserDetailsDialog";
import { useAppStore } from "@hooks/useAppStore";
import { useDevice } from "@hooks/useDevice";
import { ConfirmDialog } from "components/ConfirmDialog";
import { ErrorDialog } from "components/ErrorDialog";
import { OLXExamEligibilityDialog } from "../../../components/OLXExamEligibilityDialog";
/* -----------------Routing--------------- */
import { PurchaseDialog } from "../components/PurchaseDialog";
import { resumeSessionInfoDialogConfig } from "../constants";
import { usePracticeTests } from "../hooks/usePracticeTests";

const ResumeSessionInfoDialog = ({
	showResumeSessionInfoDialog,
	setShowResumeSessionInfoDialog,
	exam,
	creatingSession,
	handleActiveSession,
}: {
	showResumeSessionInfoDialog: boolean;
	setShowResumeSessionInfoDialog: (show: boolean) => void;
	exam: PracticeTestExam;
	creatingSession?: boolean;
	handleActiveSession: (examId: string, numericId?: number) => void;
}) => {
	const confirmDialogConfig = resumeSessionInfoDialogConfig({
		examName: exam.title,
		totalTime: exam.totalTime,
		timeElapsed: exam.timeElapsed,
	});
	return (
		<ConfirmDialog
			visible={showResumeSessionInfoDialog}
			dismissCallback={() => setShowResumeSessionInfoDialog(false)}
			title={confirmDialogConfig.title}
			body={confirmDialogConfig.body}
			icon="play-pause"
			confirmCallback={() => handleActiveSession(exam.id, exam.numericId)}
			confirmCtaLoading={creatingSession}
			confirmCta="Resume"
			dismissCta="Cancel"
		/>
	);
};

const PracticeTestsContainer = () => {
	const {
		handlePracticeTestSession,
		selectedExamId,
		creatingSession,
		showPurchaseDialog,
		toggleShowPurchaseDialog,
		showErrorDialog,
		setShowErrorDialog,
		showCollectUserDetailsDialog,
		toggleShowCollectUserDetailsDialog,
		olxError,
		setOlxError,
		selectedNumericId,
		showResumeSessionInfoDialog,
		setShowResumeSessionInfoDialog,
		handleActiveSession,
		mixedItemDistResponse,
		loading,
	} = usePracticeTests();

	const {
		currentExamContext: { examDate, examName },
	} = useExamContext();

	const practiceTestLabel = useAppStore(
		(state) => state.exam.practiceTest?.label,
	);
	const diagnosticLabel = useAppStore((state) => state.exam.diagnosticLabel);

	const { smDown } = useBreakpoints();
	const finalHeaderTitle = smDown
		? ((examDate || practiceTestLabel) ?? "Practice Tests")
		: appBarMeta["practice-tests"].title(practiceTestLabel);
	const finalSubtitle = smDown
		? ""
		: appBarMeta["practice-tests"].description(practiceTestLabel);

	const { isMobile } = useDevice();

	const CustomLoader = () => {
		return (
			<Box flex={1}>
				<CustomAppbar
					back
					options={{
						headerTitle: finalHeaderTitle,
						subtitle: finalSubtitle,
					}}
					role="banner"
				/>
				<Loader />
			</Box>
		);
	};
	if (!mixedItemDistResponse || loading) {
		return <CustomLoader />;
	}

	const data = mixedItemDistResponse;

	const {
		sections,
		stats,
		hasAccess: hasProductAccess,
	} = getPracticeTestsData(data, examName, examDate, diagnosticLabel);
	const Wrapper = isWeb ? ScrollContainer : Box;

	const selectedExamForResume = selectedNumericId
		? data?.exams?.find((exam) => exam.numericId === selectedNumericId)
		: undefined;

	return (
		<>
			<CustomAppbar
				back={isWeb ? true : !isMobile}
				options={{
					headerTitle: finalHeaderTitle,
					subtitle: finalSubtitle,
				}}
				stats={stats}
				role="banner"
			/>
			<Wrapper
				style={{
					flexGrow: 1,
					paddingVertical: 24,
				}}
				// biome-ignore lint/a11y/useSemanticElements: <explanation>
				role="main"
			>
				<PracticeTestsView
					sections={sections}
					handleNavigation={handlePracticeTestSession}
					selectedExamId={
						showResumeSessionInfoDialog ? undefined : selectedExamId
					}
					disableCard={creatingSession}
					hasProductAccess={hasProductAccess}
				/>
				{showPurchaseDialog && (
					<PurchaseDialog
						visible={showPurchaseDialog}
						handleClose={toggleShowPurchaseDialog}
						productType="PRACTICE_TEST"
						fromPracticeTestPage
					/>
				)}
				{showErrorDialog && (
					<ErrorDialog
						visible={showErrorDialog}
						handleClose={() => setShowErrorDialog(false)}
						title="Error"
						message="Session creation failed. Please try again."
					/>
				)}
				{!!olxError && (
					<OLXExamEligibilityDialog
						title={olxError.title}
						message={olxError.message}
						visible={!!olxError}
						handleClose={() => setOlxError(null)}
					/>
				)}

				{showCollectUserDetailsDialog && (
					<CollectUserDetailsDialog
						visible={showCollectUserDetailsDialog}
						handleClose={toggleShowCollectUserDetailsDialog}
					/>
				)}
				{showResumeSessionInfoDialog && selectedExamForResume && (
					<ResumeSessionInfoDialog
						showResumeSessionInfoDialog={showResumeSessionInfoDialog}
						setShowResumeSessionInfoDialog={setShowResumeSessionInfoDialog}
						exam={selectedExamForResume}
						creatingSession={creatingSession}
						handleActiveSession={handleActiveSession}
					/>
				)}
			</Wrapper>
		</>
	);
};

export default PracticeTestsContainer;
